import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CreatePostModal = ({ onClose, onCreatePost }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'general', label: 'General', icon: 'MessageSquare' },
    { value: 'achievement', label: 'Achievement', icon: 'Trophy' },
    { value: 'question', label: 'Question', icon: 'HelpCircle' },
    { value: 'challenge', label: 'Challenge', icon: 'Target' },
    { value: 'motivation', label: 'Motivation', icon: 'Zap' },
    { value: 'event', label: 'Event', icon: 'Calendar' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.title?.trim()) {
      setError('Please enter a title');
      return;
    }

    if (!formData?.content?.trim()) {
      setError('Please enter content');
      return;
    }

    setLoading(true);
    const { error: submitError } = await onCreatePost(formData);
    setLoading(false);

    if (submitError) {
      setError(submitError?.message || 'Failed to create post');
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground">Create New Post</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-red-500)" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Category
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories?.map((category) => (
                <button
                  key={category?.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: category?.value }))}
                  className={`
                    flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all
                    ${formData?.category === category?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:border-primary/50'
                    }
                  `}
                >
                  <Icon name={category?.icon} size={18} />
                  <span className="text-sm font-medium">{category?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              placeholder="Give your post a title..."
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.title?.length}/100 characters
            </p>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Content *
            </label>
            <textarea
              value={formData?.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e?.target?.value }))}
              placeholder="Share your thoughts with the community..."
              rows={8}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.content?.length}/2000 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-muted text-foreground hover:bg-muted/80"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData?.title?.trim() || !formData?.content?.trim()}
              iconName={loading ? 'Loader2' : 'Send'}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Creating...' : 'Create Post'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostModal;